import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Icon from './Icon';
import { useNavigate, useLocation } from 'react-router-dom';
import PaywallModal from './PaywallModal';
import TherapistBookingModal from './TherapistBookingModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ── Feeling score sparkline ──────────────────────────────────────────────────
const FeelingSparkline = ({ scores }) => {
  if (!scores || scores.length === 0) return null;
  const W = 200, H = 36, PAD = 6;
  const latestScore = scores[scores.length - 1]?.score ?? 5;
  const color = latestScore <= 3 ? '#16a34a' : latestScore <= 6 ? '#ca8a04' : '#dc2626';

  if (scores.length === 1) {
    const cy = PAD + ((scores[0].score - 1) / 9) * (H - PAD * 2);
    return (
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 'auto' }}>
        <line x1={0} y1={cy} x2={W} y2={cy} stroke={color} strokeWidth="1" strokeDasharray="4 4" opacity="0.3" />
        <circle cx={W / 2} cy={cy} r="5" fill={color} opacity="0.9" />
      </svg>
    );
  }

  const pts = scores.map((s, i) => [
    PAD + (i / (scores.length - 1)) * (W - PAD * 2),
    PAD + ((s.score - 1) / 9) * (H - PAD * 2),
  ]);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 'auto' }}>
      <polyline points={pts.map(p => p.join(',')).join(' ')} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map(([cx, cy], i) => (
        <circle key={i} cx={cx} cy={cy} r={i === pts.length - 1 ? 4 : 3} fill={color} />
      ))}
    </svg>
  );
};

// ── Wellness label helpers ───────────────────────────────────────────────────
const wellnessLabel = (score) => {
  if (score === null || score === undefined) return { label: '–', color: 'text-gray-400', bg: 'bg-gray-50', border: 'border-gray-200' };
  if (score <= 3) return { label: 'Mår bra', color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' };
  if (score <= 6) return { label: 'Normalt', color: 'text-yellow-700', bg: 'bg-yellow-50', border: 'border-yellow-200' };
  return { label: 'Behöver uppmärksamhet', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' };
};

const trendLabel = (scores) => {
  if (!scores || scores.length < 2) return null;
  const recent = scores[scores.length - 1].score;
  const prev = scores[Math.max(0, scores.length - 4)].score;
  const diff = recent - prev;
  if (Math.abs(diff) <= 1) return { icon: '→', text: 'Stabilt', color: 'text-gray-500' };
  if (diff < 0) return { icon: '↑', text: 'Förbättring', color: 'text-green-600' }; // lower score = better = curve goes UP
  return { icon: '↓', text: 'Försämring', color: 'text-red-500' };
};

const categoryEmoji = { pattern: '🔁', origin: '🌱', trigger: '⚡', coping: '🛡️', goal: '🎯' };

// ── Översikt tab component ───────────────────────────────────────────────────
const OverviewTab = ({ chatStats, feelingScores, chatInsights, lifeThemesOverview, navigate }) => {
  const latestScore = feelingScores.length > 0 ? feelingScores[feelingScores.length - 1].score : null;
  const wellness = wellnessLabel(latestScore);
  const trend = trendLabel(feelingScores);

  // Smart next-step recommendations
  const nextSteps = useMemo(() => {
    const steps = [];

    // Prioritet 1: Har aldrig chattat → Starta riktad session
    const hasNeverChatted = !chatStats || chatStats.total_messages === 0;
    if (hasNeverChatted) {
      steps.push({ icon: 'message-circle', text: 'Starta chat', sub: 'Välj ett område – AI:n anpassar stödet efter dig', action: () => navigate('/client/symptom-selector') });
    }

    // Prioritet 2: Inga livsteman utforskade → Utforska livsteman
    const hasExploredAnyTheme = lifeThemesOverview?.themes?.some(t => t.answered_questions > 0);
    if (!hasExploredAnyTheme) {
      steps.push({ icon: 'compass', text: 'Utforska livsteman', sub: 'Lär känna dina mönster och teman', action: () => navigate('/client/life-themes') });
    }

    // Prioritet 3: Inaktiv den senaste veckan
    if (!hasNeverChatted && chatStats?.messages_last_week === 0) {
      steps.push({ icon: 'message-circle', text: 'Dags för en check-in', sub: 'Du har inte chattat den senaste veckan', action: () => navigate('/client/chat') });
    }

    // Prioritet 4: Mående försämras – utforska relevant livstema
    if (feelingScores.length >= 2) {
      const recent = feelingScores[feelingScores.length - 1].score;
      const prev = feelingScores[Math.max(0, feelingScores.length - 3)].score;
      if (recent - prev >= 2) {
        const highTheme = lifeThemesOverview?.themes?.sort((a, b) => (b.elevated_score ?? -1) - (a.elevated_score ?? -1))[0];
        steps.push({ icon: 'trending-up', text: `Utforska "${highTheme?.theme_name || 'ett livstema'}"`, sub: 'Ditt mående visar en stigande trend', action: () => navigate(highTheme ? `/client/life-themes/${highTheme.theme_id}/progress` : '/client/life-themes') });
      }
    }

    // Fallback: riktad session
    if (steps.length === 0) {
      steps.push({ icon: 'stethoscope', text: 'Starta en riktad session', sub: 'Välj ett specifikt tema att arbeta med', action: () => navigate('/client/symptom-selector') });
    }

    return steps.slice(0, 3);
  }, [chatStats, feelingScores, lifeThemesOverview, navigate]);

  // Top impacted life themes
  const topThemes = useMemo(() =>
    (lifeThemesOverview?.themes || [])
      .filter(t => t.answered_questions > 0 && t.elevated_score != null)
      .sort((a, b) => b.elevated_score - a.elevated_score)
      .slice(0, 3),
    [lifeThemesOverview]
  );

  return (
    <div className="space-y-5">

      {/* Quick action */}
      <button
        onClick={() => navigate('/client/symptom-selector')}
        className="w-full flex items-center gap-4 p-5 bg-white border border-gray-100 hover:border-brand-primary-200 hover:bg-brand-primary-50 rounded-xl shadow-sm transition-all text-left group"
      >
        <div className="w-11 h-11 bg-brand-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
          <Icon name="chat" className="text-brand-primary-600" size={20} />
        </div>
        <div className="flex-1">
          <p className="font-bold text-gray-900 text-base">Starta chat</p>
          <p className="text-xs text-gray-500 mt-0.5">Välj ett område – AI:n anpassar stödet efter dig</p>
        </div>
        <span className="text-gray-300 group-hover:text-brand-primary-600 text-lg transition-colors">→</span>
      </button>

      {/* Row 1: Wellness + Activity */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

        {/* Wellness card */}
        <div className={`rounded-xl border p-5 ${wellness.bg} ${wellness.border}`}>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Ditt mående</p>
          {latestScore !== null ? (
            <>
              <div className="flex items-end gap-3 mb-1">
                <span className={`text-4xl font-bold ${wellness.color}`}>{latestScore}<span className="text-xl font-normal text-gray-400">/10</span></span>
                {trend && <span className={`text-sm font-medium mb-1 ${trend.color}`}>{trend.icon} {trend.text}</span>}
              </div>
              <p className={`text-sm font-medium mb-3 ${wellness.color}`}>{wellness.label}</p>
              <FeelingSparkline scores={feelingScores} />
              <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                <span>↑ Mår bra</span>
                <span>{feelingScores.length} check-in{feelingScores.length !== 1 ? 's' : ''}</span>
                <span>↓ Kris</span>
              </div>
            </>
          ) : (
            <div className="flex flex-col gap-2 py-2">
              <p className="text-sm text-gray-500">Ingen data ännu</p>
              <p className="text-xs text-gray-400">Check-in scores sparas när AI:n frågar hur du mår under chatten.</p>
            </div>
          )}
        </div>

        {/* Activity card */}
        <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Din aktivitet</p>
          <div className="flex gap-6 mb-4">
            <div>
              <p className="text-3xl font-bold text-gray-900">{chatStats?.total_messages ?? '–'}</p>
              <p className="text-xs text-gray-500">meddelanden totalt</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-brand-primary-700">{chatStats?.messages_last_week ?? '–'}</p>
              <p className="text-xs text-gray-500">senaste veckan</p>
            </div>
          </div>
          {chatStats?.first_conversation && (
            <p className="text-xs text-gray-400">
              Aktiv sedan {new Date(chatStats.first_conversation).toLocaleDateString('sv-SE', { year: 'numeric', month: 'long' })}
            </p>
          )}
          <div className="mt-4">
            <button
              onClick={() => navigate('/client/symptom-selector')}
              className="w-full py-2 text-sm font-medium text-brand-primary-700 bg-brand-primary-50 hover:bg-brand-primary-100 rounded-lg transition-colors"
            >
              Starta chat →
            </button>
          </div>
        </div>
      </div>

      {/* Row 2: Life themes in focus */}
      {topThemes.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-gray-900">Livsteman i fokus</p>
            <button onClick={() => navigate('/client/life-themes')} className="text-xs text-brand-primary-600 hover:underline">Visa alla →</button>
          </div>
          <div className="space-y-3">
            {topThemes.map(theme => {
              const score = theme.elevated_score;
              const highlight = score >= 30
                ? { bar: 'bg-red-500', badge: 'bg-red-100 text-red-700', label: 'Hög påverkan' }
                : score >= 15
                ? { bar: 'bg-yellow-400', badge: 'bg-yellow-100 text-yellow-700', label: 'Måttlig påverkan' }
                : { bar: 'bg-green-500', badge: 'bg-green-100 text-green-700', label: 'Låg påverkan' };
              const progress = theme.total_questions > 0 ? Math.round((theme.answered_questions / theme.total_questions) * 100) : 0;
              return (
                <button
                  key={theme.theme_id}
                  onClick={() => navigate(`/client/life-themes/${theme.theme_id}/progress`)}
                  className="w-full flex items-center gap-3 group text-left"
                >
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${highlight.bar}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-gray-800 truncate group-hover:text-brand-primary-700 transition-colors">{theme.theme_name}</span>
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${highlight.badge}`}>{highlight.label}</span>
                    </div>
                    <div className="mt-1.5 h-1.5 bg-gray-100 rounded-full">
                      <div className={`h-1.5 rounded-full transition-all ${highlight.bar}`} style={{ width: `${progress}%` }} />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Row 3: Saved insights */}
      {chatInsights.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
          <p className="text-sm font-semibold text-gray-900 mb-4">Dina insikter</p>
          <div className="space-y-3">
            {chatInsights.map((insight, i) => (
              <div key={insight.id || i} className="flex gap-3 items-start p-3 bg-gray-50 rounded-lg">
                <span className="text-lg flex-shrink-0">{categoryEmoji[insight.category] || '💡'}</span>
                <div className="min-w-0">
                  <p className="text-sm text-gray-800 leading-snug">{insight.text}</p>
                  <p className="text-[11px] text-gray-400 mt-1">
                    {insight.created_at ? new Date(insight.created_at).toLocaleDateString('sv-SE') : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Row 4: Next steps */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
        <p className="text-sm font-semibold text-gray-900 mb-4">Nästa steg</p>
        <div className="space-y-2">
          {nextSteps.map((step, i) => (
            <button
              key={i}
              onClick={step.action}
              className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-brand-primary-300 hover:bg-brand-primary-50 transition-all text-left group"
            >
              <div className="w-8 h-8 rounded-lg bg-brand-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-brand-primary-200 transition-colors">
                <Icon name={step.icon} size={16} className="text-brand-primary-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 group-hover:text-brand-primary-700">{step.text}</p>
                <p className="text-xs text-gray-500">{step.sub}</p>
              </div>
              <Icon name="chevron-right" size={16} className="text-gray-300 group-hover:text-brand-primary-500 flex-shrink-0 transition-colors" />
            </button>
          ))}
        </div>
      </div>

    </div>
  );
};

const PatientDashboard = ({ token, user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);
  const [showTherapistSuggestions, setShowTherapistSuggestions] = useState(false);
  const [savedMessages, setSavedMessages] = useState([]);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  
  // State for data
  const [therapist, setTherapist] = useState(null);
  const [availableTherapists, setAvailableTherapists] = useState([]);
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState(null);
  const [recentSessions, setRecentSessions] = useState([]);
  const [chatStats, setChatStats] = useState(null);
  const [credits, setCredits] = useState(null);
  const [lifeThemesOverview, setLifeThemesOverview] = useState(null);
  const [feelingScores, setFeelingScores] = useState([]);
  const [chatInsights, setChatInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Connect form state
  const [connectForm, setConnectForm] = useState({
    invitation_code: ''
  });

  // Helper functions
  const getTherapistInitials = (therapist) => {
    if (!therapist.name) return 'T';
    const names = therapist.name.split(' ');
    return names.map(name => name.charAt(0)).join('').toUpperCase().slice(0, 2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleTimeString('sv-SE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getProgressColor = (value) => {
    if (value >= 70) return 'text-green-600 bg-green-100';
    if (value >= 40) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getMoodColor = (mood) => {
    const moodColors = {
      'excellent': 'text-green-600 bg-green-100',
      'good': 'text-blue-600 bg-blue-100',
      'neutral': 'text-gray-600 bg-gray-100',
      'poor': 'text-orange-600 bg-orange-100',
      'critical': 'text-red-600 bg-red-100'
    };
    return moodColors[mood] || 'text-gray-600 bg-gray-100';
  };

  // Life themes helper functions
  const getTrafficLight = (elevatedScore) => {
    if (elevatedScore === null || elevatedScore === undefined) {
      return { color: 'bg-gray-400', label: 'Ej besvarat', textColor: 'text-gray-700' };
    }
    if (elevatedScore >= 30) {
      return { color: 'bg-red-500', label: 'Hög påverkan', textColor: 'text-red-700' };
    }
    if (elevatedScore >= 15) {
      return { color: 'bg-yellow-400', label: 'Måttlig påverkan', textColor: 'text-yellow-700' };
    }
    return { color: 'bg-green-500', label: 'Låg påverkan', textColor: 'text-green-700' };
  };

  const getProgressPercentage = (theme) => {
    if (!theme || theme.total_questions === 0) return 0;
    return Math.round((theme.answered_questions / theme.total_questions) * 100);
  };

  useEffect(() => {
    loadData();
  }, [token]);

  useEffect(() => {
    // Handle location state from express signup
    if (location.state) {
      if (location.state.message) {
        setSuccess(location.state.message);
        setShowWelcomeMessage(true);
        // Clear the message after 5 seconds
        setTimeout(() => {
          setShowWelcomeMessage(false);
          setSuccess('');
        }, 5000);
      }
      
      if (location.state.savedMessages) {
        setSavedMessages(location.state.savedMessages);
      }
      
      if (location.state.showTherapistSuggestions) {
        setShowTherapistSuggestions(true);
      }
      
      // Clear the location state to prevent re-showing on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadTherapistInfo(),
        loadRecentSessions(),
        loadChatStats(),
        loadCredits(),
        loadLifeThemesOverview(),
        loadFeelingScores(),
        loadChatInsights(),
      ]);
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadFeelingScores = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/ultra-secure-chat/feeling-scores`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setFeelingScores(response.data.scores || []);
    } catch (error) {
      console.error('Error loading feeling scores:', error);
    }
  };

  const loadChatInsights = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/ultra-secure-chat/chat-insights?limit=4`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setChatInsights(response.data.insights || []);
    } catch (error) {
      console.error('Error loading chat insights:', error);
    }
  };

  const loadCredits = async () => {
    try {
      console.log('🔍 Loading credits...');
      const response = await fetch(`${API_BASE}/api/v1/credits/balance`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const creditData = await response.json();
        setCredits(creditData.current_balance);
      } else {
        console.warn('Could not load credit balance, response:', response.status);
        setCredits(0);
      }
    } catch (error) {
      console.error('❌ Error loading credits:', error);
      setCredits(0);
    }
  };

  const loadTherapistInfo = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/invitations/my-therapist`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.has_therapist) {
        setTherapist(response.data.therapist);
      }
    } catch (error) {
      console.error('Error loading therapist info:', error);
    }
  };

  const loadRecentSessions = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/sessions/sessions?limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setRecentSessions(response.data || []);
    } catch (error) {
      console.error('Error loading recent sessions:', error);
    }
  };

  const loadChatStats = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/ultra-secure-chat/ultra-secure-chat/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setChatStats(response.data);
    } catch (error) {
      console.error('Error loading chat stats:', error);
      // Set default values if API fails
      setChatStats({
        total_messages: 0,
        messages_last_week: 0,
        first_conversation: null,
        patient_since: null
      });
    }
  };

  const loadLifeThemesOverview = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/life-theme-identifier/scores`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setLifeThemesOverview(response.data);
    } catch (error) {
      console.error('Error loading life themes overview:', error);
      setLifeThemesOverview(null);
    }
  };

  const handleConnectToTherapist = async () => {
    if (!connectForm.invitation_code.trim()) {
      setError('Ange en inbjudningskod');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.post(
        `${API_BASE}/api/invitations/use-invitation`,
        { invitation_code: connectForm.invitation_code.toUpperCase() },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setSuccess(response.data.message);
      setTherapist(response.data.therapist);
      setConnectForm({ invitation_code: '' });
      setShowConnectForm(false);
      
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunde inte ansluta till terapeut');
    } finally {
      setLoading(false);
    }
  };

    if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64 bg-white rounded-lg shadow-sm ">
        <div className="text-center">
          <Icon name="loading" size={32} className="animate-spin mx-auto mb-4 text-brand-primary-500" />
          <p className="text-secondary text-lg">Laddar din dashboard...</p>
        </div>
      </div>
    );
  }  return (
    <div className="min-h-screen">
      {/* Success/Error Messages */}
      {error && (
        <div className="bg-error-50 border border-error-600 rounded-lg p-4 mb-4">
          <p className="text-error-700">⚠️ {error}</p>
        </div>
      )}
      
      {success && (
        <div className="bg-success-50 border border-success-600 rounded-lg p-4 mb-4">
          <p className="text-success-700">✅ {success}</p>
        </div>
      )}

      {/* Welcome Banner for New Users */}
      {showWelcomeMessage && (
        <div className="bg-gradient-to-r from-brand-primary-500 to-brand-primary-600 text-white rounded-2xl p-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold mb-2">🎉 Välkommen till Your Therapist!</h2>
              <p className="mb-4 opacity-90">
                Ditt konto är skapat och du är nu inloggad. {savedMessages.length > 0 && `Dina ${savedMessages.length} meddelanden har sparats och väntar på dig.`}
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name="message-circle-heart" size={16} className="text-white" />
                    <span className="font-medium text-sm">AI-Stöd 24/7</span>
                  </div>
                  <p className="text-xs opacity-90">Fortsätt chatta när du behöver stöd</p>
                </div>
                
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name="users" size={16} className="text-white" />
                    <span className="font-medium text-sm">Mänskliga Terapeuter</span>
                  </div>
                  <p className="text-xs opacity-90">Frivilligt - bara när du vill</p>
                </div>
                
                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name="sparkles" size={16} className="text-white" />
                    <span className="font-medium text-sm">Personliga Insikter</span>
                  </div>
                  <p className="text-xs opacity-90">AI analyserar dina framsteg</p>
                </div>
              </div>
            </div>
            
            <button
              onClick={() => setShowWelcomeMessage(false)}
              className="text-white/80 hover:text-white transition-colors"
            >
              <Icon name="x" size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Therapist Suggestions */}
      {showTherapistSuggestions && availableTherapists.length > 0 && (
        <div className="bg-info-50 border border-info-500 rounded-2xl p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-info-700 mb-2">
                💙 Förslag på legitimerade terapeuter
              </h3>
              <p className="text-info-600 mb-4">
                Baserat på din profil har vi hittat terapeuter som kan passa dig. 
                <span className="font-medium"> Det är helt frivilligt</span> - AI-terapin fungerar utmärkt på egen hand.
              </p>
            </div>
            <button
              onClick={() => setShowTherapistSuggestions(false)}
              className="text-info-600 hover:text-info-700 transition-colors"
            >
              <Icon name="x" size={20} />
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
            {availableTherapists.slice(0, 3).map((therapist) => (
              <div key={therapist.id} className="bg-white border border-neutral-300 rounded-lg p-4">
                <div className="flex items-start space-x-3 mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                    {getTherapistInitials(therapist)}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{therapist.name}</h4>
                    <p className="text-sm text-gray-600">{therapist.specializations?.[0] || 'Allmän terapi'}</p>
                  </div>
                </div>
                
                <p className="text-sm text-gray-700 mb-3 line-clamp-2">
                  {therapist.bio || 'Erfaren terapeut med fokus på personcentrerad vård.'}
                </p>
                
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {therapist.patients_count || 0} aktiva patienter
                  </span>
                  <button
                    onClick={() => {
                      setShowConnectForm(true);
                      setShowTherapistSuggestions(false);
                    }}
                    className="text-brand-primary-600 hover:text-brand-primary-700 text-sm font-medium"
                  >
                    Kontakta →
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          <div className="bg-brand-primary-100 border border-brand-primary-300 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <Icon name="heart" size={16} className="text-brand-primary-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-brand-primary-800 mb-1">Kom ihåg</p>
                <p className="text-xs text-brand-primary-700">
                  Du kan fortsätta använda AI-terapin obegränsat även om du väljer att kontakta en mänsklig terapeut. 
                  Många användare kombinerar båda för bästa resultat.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Welcome Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-primary">
            Välkommen tillbaka, {user?.first_name || user?.name || 'Klient'}!
          </h1>
          <p className="text-secondary">Här är din personliga översikt över din välbefinnanderesa.</p>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-6">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === 'overview'
                  ? 'border-brand-primary-600 text-brand-primary-700'
                  : 'border-transparent text-tertiary hover:text-primary hover:border-neutral-400'
              }`}
            >
              <span>Översikt</span>
            </button>
            <button
              onClick={() => setActiveTab('life_themes')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === 'life_themes'
                  ? 'border-brand-primary-600 text-brand-primary-700'
                  : 'border-transparent text-tertiary hover:text-primary hover:border-neutral-400'
              }`}
            >
              <span>Livsteman</span>
            </button>
            <button
              onClick={() => setActiveTab('therapist')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === 'therapist'
                  ? 'border-brand-primary-600 text-brand-primary-700'
                  : 'border-transparent text-tertiary hover:text-primary hover:border-neutral-400'
              }`}
            >
              <span>Min Terapeut</span>
            </button>
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <OverviewTab
            chatStats={chatStats}
            feelingScores={feelingScores}
            chatInsights={chatInsights}
            lifeThemesOverview={lifeThemesOverview}
            navigate={navigate}
          />
        )}

        {/* Life Themes Tab */}
        {activeTab === 'life_themes' && (
          <div className="space-y-6">

            {/* Life Themes Overview */}
            {lifeThemesOverview && lifeThemesOverview.themes && lifeThemesOverview.themes.length > 0 && (
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-medium text-primary">Mina Livsteman</h3>
                    <button
                      onClick={() => navigate('/client/life-themes')}
                      className="text-sm font-medium text-brand-primary-600 hover:text-brand-primary-700 transition-colors"
                    >
                      Fortsätt utforska →
                    </button>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-neutral-100 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-primary">{lifeThemesOverview.total_themes}</div>
                      <div className="text-xs text-secondary">Totalt teman</div>
                    </div>
                    <div className="bg-success-50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-success-700">{lifeThemesOverview.completed_themes}</div>
                      <div className="text-xs text-success-600">Slutförda</div>
                    </div>
                    <div className="bg-brand-primary-100 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-brand-primary-800">{lifeThemesOverview.total_answered}</div>
                      <div className="text-xs text-brand-primary-700">Besvarade frågor</div>
                    </div>
                    <div className="bg-brand-tertiary-100 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-brand-tertiary-700">{Math.round(lifeThemesOverview.overall_progress_percentage)}%</div>
                      <div className="text-xs text-brand-tertiary-600">Progress</div>
                    </div>
                  </div>

                  {/* Themes Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {lifeThemesOverview.themes
                      .sort((a, b) => {
                        // Sort by elevated_score descending (highest impact first)
                        // Treat null/undefined as -1 to put them at the end
                        const scoreA = a.elevated_score ?? -1;
                        const scoreB = b.elevated_score ?? -1;
                        return scoreB - scoreA;
                      })
                      .map((theme) => {
                      const trafficLight = getTrafficLight(theme.elevated_score);
                      const progress = getProgressPercentage(theme);
                      const isCompleted = theme.answered_questions === theme.total_questions;
                      const hasStarted = theme.answered_questions > 0;

                      return (
                        <button
                          key={theme.theme_id}
                          onClick={() => navigate(`/client/life-themes/${theme.theme_id}/progress`)}
                          className={`group text-left rounded-xl p-5 transition-all duration-200 border-2 ${
                            hasStarted
                              ? 'bg-white border-neutral-200 hover:border-brand-primary-400 hover:shadow-md'
                              : 'bg-white border-neutral-200 hover:border-brand-primary-300 hover:shadow-sm'
                          }`}
                        >
                          <div className="flex flex-col justify-between h-full space-y-3">
                            {/* Header */}
                            <div className="flex items-start justify-between gap-3">
                              <h4 className={`font-semibold text-sm leading-tight flex-1 ${
                                hasStarted ? 'text-gray-900 group-hover:text-brand-primary-700' : 'text-gray-700 group-hover:text-brand-primary-600'
                              } transition-colors`}>
                                {theme.theme_name}
                              </h4>
                              {isCompleted && (
                                <Icon name="check-circle" size={18} className="text-success-600 flex-shrink-0" />
                              )}
                            </div>
                            
                            {/* Progress Bar - Only show if not completed */}
                            {!isCompleted && (
                              <div>
                                <div className={`w-full rounded-full h-2 ${hasStarted ? 'bg-neutral-200' : 'bg-neutral-200'}`}>
                                  <div
                                    className={`h-2 rounded-full transition-all duration-300 ${
                                      hasStarted ? 'bg-brand-primary-600' : 'bg-neutral-400'
                                    }`}
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                              </div>
                            )}
                            
                            {/* Footer */}
                            <div className="flex items-center justify-between">
                              <span className={`text-xs font-medium ${hasStarted ? 'text-gray-600' : 'text-gray-500'}`}>
                                {theme.answered_questions}/{theme.total_questions} frågor
                              </span>
                              {hasStarted && theme.elevated_score !== null && theme.elevated_score !== undefined && (
                                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                                  trafficLight.color === 'bg-red-500' 
                                    ? 'bg-red-100 text-red-700' 
                                    : trafficLight.color === 'bg-yellow-400'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-green-100 text-green-700'
                                }`}>
                                  {trafficLight.label}
                                </span>
                              )}
                              {!hasStarted && (
                                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-neutral-100 text-neutral-600 group-hover:bg-brand-primary-100 group-hover:text-brand-primary-700 transition-colors">
                                  Påbörja →
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            
            
          </div>
        )}

        {/* Therapist Tab */}
        {activeTab === 'therapist' && (
          <div className="space-y-6">
            {therapist ? (
              <>
                {/* Current Therapist */}
                <div className="bg-white shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-medium text-primary">Min Terapeut</h3>
                      <span className="px-2 py-1 bg-success-100 text-success-700 rounded-full text-xs font-medium">
                        Ansluten
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <div className="w-16 h-16 bg-brand-primary-100 text-brand-primary-700 rounded-full flex items-center justify-center font-semibold text-xl">
                        {getTherapistInitials(therapist)}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-semibold text-primary">{therapist.name}</h4>
                        <div className="space-y-1 mt-2">
                          {therapist.email && (
                            <p className="text-sm text-secondary flex items-center gap-2">
                              <Icon name="mail" size={14} />
                              {therapist.email}
                            </p>
                          )}
                          {therapist.phone && (
                            <p className="text-sm text-secondary flex items-center gap-2">
                              <Icon name="phone" size={14} />
                              {therapist.phone}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Therapist Actions */}
                <div className="bg-white shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Kommunikation</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <button
                        onClick={() => navigate('/client/chat')}
                        className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 group text-left"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                            <Icon name="message-circle" size={20} />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">AI Chat</h4>
                            <p className="text-sm text-gray-500">Chatta med AI mellan sessioner</p>
                          </div>
                        </div>
                      </button>
                      <button
                        onClick={() => setShowBookingModal(true)}
                        className="p-4 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-all duration-200 group text-left"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                            <Icon name="calendar" size={20} />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">Boka samtal</h4>
                            <p className="text-sm text-gray-500">Boka tid med din terapeut</p>
                          </div>
                        </div>
                      </button>
                      <button
                        onClick={() => navigate('/client/sessions')}
                        className="p-4 border border-gray-200 rounded-lg hover:border-green-300 hover:bg-green-50 transition-all duration-200 group text-left"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-green-100 text-green-600 rounded-full flex items-center justify-center group-hover:bg-green-200 transition-colors">
                            <Icon name="file-text" size={20} />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900">Terapisessioner</h4>
                            <p className="text-sm text-gray-500">Se och hantera dina sessioner</p>
                          </div>
                        </div>
                      </button>
                      
                      
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* No Therapist Connected */
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <div className="text-center py-8">
                    <Icon name="message-circle-heart" size={64} className="mx-auto text-neutral-400 mb-6" />
                    <h3 className="text-xl font-semibold text-primary mb-4">Ingen Terapeut Ansluten</h3>
                    <p className="text-secondary mb-6 max-w-md mx-auto">
                      Du har ingen professionell terapeut ansluten ännu. Kolla din e-post efter en inbjudan eller be din terapeut om en inbjudningskod.
                    </p>
                    
                    {/* E-post invitation info */}
                    <div className="bg-info-50 border border-info-500 rounded-lg p-4 mb-6 max-w-lg mx-auto">
                      <div className="flex items-center justify-center mb-3">
                        <div className="w-12 h-12 bg-info-500 text-white rounded-full flex items-center justify-center">
                          <Icon name="mail" size={24} />
                        </div>
                      </div>
                      <h4 className="font-semibold text-info-700 mb-2">Har du fått en inbjudan via e-post?</h4>
                      <p className="text-info-600 text-sm mb-3">
                        Din terapeut skickar inbjudningar med en 8-siffrig kod direkt till din e-postadress.
                      </p>
                      <p className="text-info-600 text-xs">
                        ✉️ Kolla både inkorg och skräppost efter e-post från din terapeut
                      </p>
                    </div>
                    
                    <button
                      onClick={() => setShowConnectForm(true)}
                      className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-brand-primary-600 hover:bg-brand-primary-700 transition-colors"
                    >
                      <Icon name="mail-plus" size={20} className="mr-2" />
                      Ange Inbjudningskod
                    </button>
                    
                    <div className="mt-6 bg-info-50 border border-info-500 rounded-lg p-4 max-w-lg mx-auto">
                      <div className="text-info-700 text-sm flex flex-col gap-2">
                        <div className="flex gap-1"><Icon name="lightbulb" size={16} /><span className="font-bold">Tips</span></div>
                        <div className="text-left">Även utan en ansluten terapeut kan du använda vår AI-terapeut dygnet runt för stöd och vägledning.</div> 
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Connect to Therapist Modal */}
      {showConnectForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Anslut till din terapeut</h3>
              <button
                onClick={() => setShowConnectForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <Icon name="x" size={20} />
              </button>
            </div>
            
            {/* Email instruction */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name="mail" size={16} />
                </div>
                <div>
                  <h4 className="text-sm font-medium text-blue-900 mb-1">Kolla din e-post</h4>
                  <p className="text-sm text-blue-800">
                    Din terapeut har skickat en inbjudan till din e-postadress med en 8-siffrig kod.
                  </p>
                  <p className="text-xs text-info-600 mt-1">
                    Exempel: <code className="bg-info-500 text-white px-1 py-0.5 rounded text-xs">ABC12345</code>
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label htmlFor="invitation_code" className="block text-sm font-medium text-secondary mb-1">
                  Inbjudningskod från e-post *
                </label>
                <input
                  type="text"
                  id="invitation_code"
                  value={connectForm.invitation_code}
                  onChange={(e) => setConnectForm({...connectForm, invitation_code: e.target.value.toUpperCase()})}
                  className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary-500 focus:border-transparent font-mono tracking-wider text-center"
                  placeholder="ABC12345"
                  maxLength={8}
                />
                <p className="text-xs text-tertiary mt-1 flex items-center gap-1">
                  <Icon name="info" size={12} />
                  Finns inte i inkorgen? Kolla skräppost eller kontakta din terapeut.
                </p>
              </div>
              
              {/* Common issues */}
              <div className="bg-warning-50 border border-warning-600 rounded-lg p-3">
                <h5 className="text-xs font-medium text-warning-700 mb-2">Vanliga problem:</h5>
                <ul className="text-xs text-warning-600 space-y-1">
                  <li>• Koden har gått ut (giltig i 7 dagar)</li>
                  <li>• E-posten hamnade i skräppost</li>
                  <li>• Koden skrevs fel (använd endast stora bokstäver)</li>
                </ul>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowConnectForm(false)}
                  className="px-4 py-2 border border-neutral-300 rounded-md text-sm font-medium text-secondary hover:bg-neutral-100 transition-colors"
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  onClick={handleConnectToTherapist}
                  disabled={loading || !connectForm.invitation_code.trim()}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-brand-primary-600 hover:bg-brand-primary-700 disabled:opacity-50 transition-colors"
                >
                  {loading ? (
                    <>
                      <Icon name="loading" size={16} className="mr-2 animate-spin" />
                      Ansluter...
                    </>
                  ) : (
                    <>
                      <Icon name="message-circle-heart" size={16} className="mr-2" />
                      Anslut till terapeut
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Paywall Modal */}
      <PaywallModal 
        isOpen={showPaywall}
        onClose={() => {
          setShowPaywall(false);
          // Reload credits after closing modal (in case user made a purchase)
          loadCredits();
        }}
        credits={credits}
      />

      {/* Therapist Booking Modal */}
      <TherapistBookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        therapist={therapist}
      />
    </div>
  );
};

export default PatientDashboard;
